import { useDispatch, useSelector } from 'react-redux';
import { additionalData } from '../store/reducers/additionalData';
import {
    ctxPatientType,
    setBlockDischargeMandatoryAssessment,
    setMandatoryAssessmentList,
    ctxPatientAgeType,
} from '../store/reducers/patientContext';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import { agyleAssessmentList } from '../store/reducers/dicts';
import { formIds } from '../pages/patient/patientContext/components/forms/formsId';
import { assessmentsConfig } from '../store/reducers/externalConfig';
import { DEFAULT_ASSESSMENTS_VALUE } from '../pages/admin/aglyeConfig/configDefaultValues';
import { PATIENT_CONTEXT_TYPE, getStringAgeType } from '../util/patientContextUtil';
import { contextFormsList } from '../pages/patient/patientContext/components/forms/formsContextList';

const useAllAssessmentsFiled = (patient) => {
    const dispatch = useDispatch();

    // Selectors
    const patientAddData = useSelector(additionalData);
    const assessmentList = useSelector(agyleAssessmentList);
    const assessmentsCfg = useSelector(assessmentsConfig);
    const patientType = useSelector(ctxPatientType);
    const assCfg = useSelector(assessmentsConfig);
    const patientAgeType = useSelector(ctxPatientAgeType);

    const [filteredAssessmentsList, setFilteredAssessmentsList] = useState([]);
    const [noFilled, setNoFilled] = useState(false);
    const [assessmentsLists, setAssessmentsLists] = useState([]);

    const changeAge = (assessment) => {
        const getStringAge = getStringAgeType(patientAgeType);
        if (!_.isEmpty(assessment?.ageRestriction)) {
            return assessment?.ageRestriction.includes(getStringAge);
        }
        return true;
    };

    const prepareAssessmentList = (templatesList) => {
        const assessments = [];
        let schema = _.find(templatesList, (temp) => temp?.service?.wardsId?.[0] === patient?.admission?.pointOfCare);
        if (!schema) schema = _.find(templatesList, { id: 'default' });

        if (!_.isEmpty(schema?.assessmentList)) {
            _.forEach(schema.assessmentList, (id) => {
                const findAss = _.find(assessmentList, { code: id });
                if (findAss && changeAge(findAss)) assessments.push(findAss);
            });
        }
        setFilteredAssessmentsList(assessments);
    };

    useEffect(() => {
        switch (patientType) {
            case PATIENT_CONTEXT_TYPE.ED:
            case PATIENT_CONTEXT_TYPE.SDEC:
            case PATIENT_CONTEXT_TYPE.UTC:
                let assessmentFind = assessmentsCfg?.ED?.mandatory?.find(
                    (ass) => ass?.service?.wardsId?.[0] === patient?.admission?.pointOfCare,
                );
                if (!assessmentFind) {
                    assessmentFind = assessmentsCfg?.ED?.mandatory?.find((ass) => ass?.id === 'default');
                }
                prepareAssessmentList(assessmentsCfg?.ED?.mandatory);
                break;
            case PATIENT_CONTEXT_TYPE.EDAU:
            case PATIENT_CONTEXT_TYPE.INPATIENT: {
                const flat = _.flatMap(assessmentsCfg?.INPATIENT?.mandatory, (ass) => ass?.assessmentList);
                prepareAssessmentList(_.uniq(flat));
                break;
            }

            default:
                setFilteredAssessmentsList([]);
        }
    }, [patientType, patient?.admission?.pointOfCare, patientAgeType]);

    useEffect(() => {
        let filled = true;
        const list = [];

        filteredAssessmentsList.forEach((assessment) => {
            const form = _.find(contextFormsList, { id: assessment?.code });
            if (!form?.enabled) return;

            const formContainer = formIds.find((form) => form.id === assessment.code);
            if (formContainer && assessment.active) {
                try {
                    const isAddData = formContainer?.isAddData;
                    const path = assessment?.metadata?.path ?? {};
                    const data = _.get(isAddData ? patientAddData : patient, path.filled);
                    let filteredData = !!data;
                    if (data) {
                        filteredData = _.isArray(data) ? data : [data];
                        if (isAddData) {
                            filteredData = _.filter(filteredData, (f) => {
                                const activityId = f?.attendanceId;
                                const date = _.get(f, path.date);

                                if (activityId) {
                                    return patient?.admission?.__id === activityId;
                                }
                                if (date) {
                                    const startDate = moment(patient?.admission?.startDate);
                                    const isActive = _.isNil(patient?.admission?.endDate);
                                    const endDate = isActive ? moment() : moment(patient?.admission?.startDate);

                                    return moment(date).isBetween(startDate, endDate, null, '[]');
                                }
                                return null;
                            });
                        }
                    }

                    if (_.isEmpty(filteredData)) {
                        list.push(assessment?.description);
                        if (filled) filled = false;
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        });
        setNoFilled(!filled);
        setAssessmentsLists(list);
    }, [patient, patientAddData, filteredAssessmentsList]);

    useEffect(() => {
        const mandatory = assCfg?.mandatory || DEFAULT_ASSESSMENTS_VALUE.mandatory;
        if (mandatory?.enabled) {
            dispatch(setBlockDischargeMandatoryAssessment(noFilled));
        } else {
            dispatch(setBlockDischargeMandatoryAssessment(false));
        }
    }, [noFilled, assCfg]);

    useEffect(() => {
        dispatch(setMandatoryAssessmentList(assessmentsLists));
    }, [assessmentsLists]);
};

export default useAllAssessmentsFiled;
